import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { Injector, inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';

import { AuthService } from '../services/auth.service';

// Storage key for session (must match AuthService).
const STORAGE_KEY = 'ams_session';

// Auth endpoints that must never trigger a refresh: /refresh would recurse, and a
// 401 from login/logout means what it says.
const NO_REFRESH = ['/auth/refresh', '/auth/login', '/auth/logout'];

/**
 * Recovers an expired access token instead of ending the session.
 *
 * MUST BE REGISTERED LAST. Interceptors handle a response in reverse order, so the
 * last one registered sees the error first — which is the only way this can act
 * before errorInterceptor clears the session and redirects to /launcher. Move it
 * earlier in the array and it still compiles, still looks right, and silently never
 * runs: every expiry goes back to logging the user out.
 *
 * On a 401 it asks AuthService for a fresh token (that call is single-flight, so a
 * page firing several requests refreshes once) and replays the original request with
 * it. Only if the refresh fails does the error continue outward, where the existing
 * logout handling takes over unchanged.
 */
export const refreshInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  // Resolved lazily, per request: AuthService depends on HttpClient, and taking it
  // at module scope would build that graph while the chain is still assembling.
  const injector = inject(Injector);

  if (NO_REFRESH.some(path => req.url.includes(path))) {
    return next(req);
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401) {
        return throwError(() => error);
      }

      // No session, or no refresh token in it, means this was an anonymous call —
      // public settings, the public storefront, an invitation link. There is nothing
      // to refresh and nothing to log out of, so let the 401 through untouched.
      const session = readSession();
      if (!session?.refreshToken) {
        return throwError(() => error);
      }

      // A 401 answering a request that carried a superseded token is stale — the
      // session has already moved on, and refreshing on its behalf would rotate a
      // perfectly good token for nothing. Same guard errorInterceptor applies.
      const requestToken = req.headers.get('Authorization')?.replace('Bearer ', '');
      if (requestToken && requestToken !== session.accessToken) {
        return throwError(() => error);
      }

      const auth = injector.get(AuthService);
      return auth.refreshToken().pipe(
        switchMap(accessToken =>
          // The header on `req` still carries the token that just expired, so the
          // replay has to set the new one explicitly.
          next(req.clone({ setHeaders: { Authorization: `Bearer ${accessToken}` } }))
        ),
        catchError(() => throwError(() => error)),
      );
    })
  );
};

/** Reads the session the same way the other interceptors do, straight from storage. */
function readSession(): { accessToken?: string; refreshToken?: string } | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}
